import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

/**
 * Hook for managing query parameters between search/category pages and detail view.
 * Provides a single point of definition for bidirectional parameter communication.
 *
 * @returns {Object} Object containing:
 *   - getProductLinkParams: Function to prepare params for product links (TO detail view)
 *   - backLink: The computed back link URL (FROM detail view)
 */
export const useQueryParametersForDetailViewBackLink = (defaultBackLink = '/') => {
  const searchParams = useSearchParams();

  // Build initial back link from nav-q or fallback
  const buildBackLink = useCallback((): string => {
    const navQ = searchParams?.get('nav-q');
    return navQ ? `/s/${navQ}` : defaultBackLink;
  }, [searchParams, defaultBackLink]);

  // State for back link (may be updated from sessionStorage)
  const [backLink, setBackLink] = useState<string>(buildBackLink);

  // Check sessionStorage for remembered page data on mount
  useEffect(() => {
    const pageData = window.sessionStorage.getItem('rememberedPageData');
    if (pageData) {
      const rememberedData = JSON.parse(pageData);
      setBackLink(rememberedData.path);
    }
  }, []);

  /**
   * Prepares query parameters for product links (going TO detail view).
   * Adds nav-q with search term for tracking purposes.
   */
  const getProductLinkParams = useCallback(
    (
      currentQuery: Record<string, string | string[] | undefined>,
      searchQuery?: string,
    ): Record<string, string | string[] | undefined> => ({
      ...currentQuery,
      ...(searchQuery && { 'nav-q': searchQuery }),
    }),
    [],
  );

  return {
    getProductLinkParams,
    backLink,
  };
};
